import React, { useState } from 'react';
import { QueueStore } from '../queues/store/queue-store';
import { useEventNotificationsStore } from '../event-notifications/use-event-notifications';
import { useApiClient } from '@m43/core';
import { ScheduledEventQueueRowData } from '../socket/stream-types';
import { NudgeObject, QueueViewsResource } from '@mark43/cad-api';
import {
    CadNextSocketListener,
    createStreamListener,
    useSocketStatus,
    useStream,
} from '../socket/use-stream';
import { WorkspacePanel } from '../panels/workspace-panel';
import { workspacePanelIsQueue } from '../panels/workspace-queue-exists';
import { useQuery } from '@tanstack/react-query';
import { toBooleanMap } from '../common/helpers/utils';
import { handleStoreUpdates } from '../queues/utils/handle-store-updates';
import { PromiseBuffer } from '../common/promises/promise-buffer';
import { useDispatchAreaStore } from '../dispatch-areas/use-dispatch-areas';

/**
 * This should never be imported directly outside of scheduled-event-queue-state-provider
 */
export function useIntegratedScheduledEventQueueStore({
    workspacePanel,
    dispatchAreaIds,
}: {
    workspacePanel: WorkspacePanel;
    dispatchAreaIds: number[];
}): QueueStore<ScheduledEventQueueRowData> {
    workspacePanelIsQueue(workspacePanel);
    const eventNotificationsStore = useEventNotificationsStore();
    const dispatchAreaStore = useDispatchAreaStore();

    const [promiseBuffer] = React.useState(
        () =>
            new PromiseBuffer<number>({
                delay: 100,
                callback: (inputs) => {
                    return fetchScheduledEventUpdatesByIds(
                        inputs,
                        dispatchAreaStore.getMonitoredAndOwnedDispatchAreaIds()
                    );
                },
            })
    );

    const store = useState(
        () =>
            new QueueStore<ScheduledEventQueueRowData>({
                workspacePanel,
                eventNotificationsStore,
                playAedSoundForEvent: () => {},
            })
    )[0];

    const apiClient = useApiClient();

    const getInitialQueueStateQuery = useQuery({
        queryKey: [
            'get-initial-scheduled-event-queue-views',
            store.workspacePanel.queue.id,
            dispatchAreaIds,
        ],
        queryFn() {
            promiseBuffer.stop();
            promiseBuffer.reset();
            return apiClient.request<QueueViewsResource.GetScheduledEventQueueViews>({
                method: 'POST',
                url: 'queue_views/scheduled_event_queue_views/',
                data: {
                    dispatchAreaIds,
                },
            });
        },
        onSuccess(response) {
            if (response.data.data?.scheduledEventQueueViews) {
                store.setRows(response.data.data.scheduledEventQueueViews);
            }
            promiseBuffer.start();
        },
        onError() {
            store.setIsError(true);
        },
        notifyOnChangeProps: [],
    });

    const refetch = getInitialQueueStateQuery.refetch;

    const socketStatusListener: CadNextSocketListener = React.useMemo(
        () => ({
            invokeOnInitialize: true,
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (socketStatusChangeEvent.status === 'CONNECTED') {
                    refetch();
                }
            },
        }),
        [refetch]
    );

    const { getSocketStatus } = useSocketStatus(socketStatusListener);

    React.useEffect(() => {
        if (getSocketStatus() === 'CONNECTED') {
            refetch();
        }
    }, [dispatchAreaIds, refetch, getSocketStatus]);

    const dispatchAreasMap = React.useMemo(() => {
        return toBooleanMap(dispatchAreaIds);
    }, [dispatchAreaIds]);

    const fetchScheduledEventUpdatesByIds = React.useMemo(() => {
        return (scheduledEventIds: number[], dispatchAreaIds: number[]) => {
            return apiClient
                .request<QueueViewsResource.GetScheduledEventQueueViewsByIds>({
                    method: 'POST',
                    url: 'queue_views/scheduled_event_queue_views/ids/',
                    data: {
                        dispatchAreaIds,
                        scheduledEventIds,
                    },
                })
                .then((response) => {
                    handleStoreUpdates(
                        store,
                        scheduledEventIds,
                        response.data.data?.scheduledEventQueueViews
                    );
                });
        };
    }, [apiClient, store]);

    const getScheduledEventIdsInStoreFromNudges = React.useMemo(() => {
        return (scheduledEventNudges: NudgeObject[] = []): number[] => {
            return scheduledEventNudges
                .filter((scheduledEventNudge) => {
                    if (store.hasRowById(scheduledEventNudge.id)) {
                        return true;
                    }
                    const passesDispatchAreaFilter =
                        scheduledEventNudge.d && dispatchAreasMap[scheduledEventNudge.d];
                    const passesIsCompleteFilter = !scheduledEventNudge.co;
                    return passesDispatchAreaFilter && passesIsCompleteFilter;
                })
                .map((scheduledEventNudge) => {
                    return scheduledEventNudge.id;
                });
        };
    }, [dispatchAreasMap, store]);

    const getScheduledEventIdsInStoreFromNonScheduledEventNudges = React.useMemo(() => {
        return (nonScheduledEventNudges: NudgeObject[] = []): number[] => {
            return nonScheduledEventNudges
                .filter((nonScheduledEventNudge) => {
                    if (!nonScheduledEventNudge.se) {
                        return false;
                    }
                    return (
                        !!nonScheduledEventNudge.se && store.hasRowById(nonScheduledEventNudge.se)
                    );
                })
                .map((nonScheduledEventNudge) => {
                    return nonScheduledEventNudge.se!;
                });
        };
    }, [store]);

    const getScheduledEventIdsToEvictFromScheduledEventNudges = React.useMemo(() => {
        return (scheduledEventNudges: NudgeObject[] = []): number[] => {
            return scheduledEventNudges
                .filter((scheduledEventNudge) => {
                    // nudge is for an event that isn't in the store, no need to evict
                    if (!store.hasRowById(scheduledEventNudge.id)) {
                        return false;
                    } else if (scheduledEventNudge.d && !dispatchAreasMap[scheduledEventNudge.d]) {
                        return true;
                    } else if (scheduledEventNudge.co) {
                        return true;
                    }
                })
                .map((scheduledEventNudge) => scheduledEventNudge.id);
        };
    }, [dispatchAreasMap, store]);

    const listener = React.useMemo(
        () =>
            createStreamListener({
                streamNames: ['nudge'],
                onMessage: (message) => {
                    const scheduledEventIdsToUpdate: number[] = [
                        ...getScheduledEventIdsInStoreFromNudges(message.payload.map['SAE']),
                        ...getScheduledEventIdsInStoreFromNonScheduledEventNudges(
                            message.payload.map['SET']
                        ),
                        ...getScheduledEventIdsInStoreFromNonScheduledEventNudges(
                            message.payload.map['SESEL']
                        ),
                        ...getScheduledEventIdsToEvictFromScheduledEventNudges(
                            message.payload.map['SAE'] ?? []
                        ),
                    ];
                    if (scheduledEventIdsToUpdate.length > 0) {
                        for (const scheduledEventId of scheduledEventIdsToUpdate) {
                            promiseBuffer.enqueue(scheduledEventId);
                        }
                    }
                },
            }),
        [
            getScheduledEventIdsInStoreFromNudges,
            getScheduledEventIdsInStoreFromNonScheduledEventNudges,
            getScheduledEventIdsToEvictFromScheduledEventNudges,
            promiseBuffer,
        ]
    );
    useStream(listener);

    return store;
}
